import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { logError } from '@/lib/logger';
import { Card } from '@/components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const providerColor = (provider) => {
  if (provider === 'abacus') return 'bg-emerald-100 text-emerald-800';
  if (provider === 'emergent') return 'bg-amber-100 text-amber-800';
  return 'bg-stone-100 text-stone-700';
};

const fmtInt = (n) => (n ?? 0).toLocaleString();
const fmtUsd = (n) => `$${(n ?? 0).toFixed(4)}`;
const fmtUsdShort = (n) => {
  const v = n ?? 0;
  if (v >= 10) return `$${v.toFixed(2)}`;
  if (v >= 0.01) return `$${v.toFixed(3)}`;
  return `$${v.toFixed(4)}`;
};

export const ModelsView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/ai/model-insights`);
      setData(res.data);
    } catch (err) {
      logError('Model insights fetch failed:', err?.response?.status);
      toast.error('Could not load model insights');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="animate-pulse text-stone-500 py-12 text-center" data-testid="models-loading">
        Loading model insights...
      </div>
    );
  }
  if (!data) {
    return <div className="text-stone-500 py-12 text-center">No data yet.</div>;
  }

  const totals = data.totals || {};
  const totalReplies = totals.assistant_messages_tagged || 0;
  const totalCost = totals.estimated_cost_usd || 0;

  return (
    <div className="space-y-6" data-testid="models-view">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6">
          <p className="text-xs uppercase tracking-wide text-stone-500">Tagged replies</p>
          <p className="text-3xl font-medium mt-1" data-testid="models-tagged-count">{fmtInt(totalReplies)}</p>
          <p className="text-xs text-stone-400 mt-2">Sage replies with model tracking.</p>
        </Card>
        <Card className="p-6">
          <p className="text-xs uppercase tracking-wide text-stone-500">Total tokens</p>
          <p className="text-3xl font-medium mt-1" data-testid="models-total-tokens">
            {fmtInt((totals.input_tokens || 0) + (totals.output_tokens || 0))}
          </p>
          <p className="text-xs text-stone-400 mt-2">
            in {fmtInt(totals.input_tokens)} · out {fmtInt(totals.output_tokens)}
          </p>
        </Card>
        <Card className="p-6" data-testid="models-total-cost-card">
          <p className="text-xs uppercase tracking-wide text-stone-500">Est. spend — all-time</p>
          <p className="text-3xl font-medium mt-1" style={{ color: '#C9A84C' }} data-testid="models-total-cost">
            {fmtUsdShort(totalCost)}
          </p>
          <p className="text-xs text-stone-400 mt-2">Public list prices; treat as order-of-magnitude.</p>
        </Card>
        <Card className="p-6" data-testid="models-30d-cost-card">
          <p className="text-xs uppercase tracking-wide text-stone-500">Est. spend — 30d</p>
          <p className="text-3xl font-medium mt-1" style={{ color: '#C9A84C' }}>
            {fmtUsdShort(totals.last_30_days_cost_usd)}
          </p>
          <p className="text-xs text-stone-400 mt-2">Rolling 30-day window.</p>
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="text-lg font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          Model usage & spend — all-time
        </h3>
        {data.by_model?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-stone-500 border-b border-stone-200">
                  <th className="py-2 pr-3">Model</th>
                  <th className="py-2 px-3 text-right">Replies</th>
                  <th className="py-2 px-3 text-right">Input tokens</th>
                  <th className="py-2 px-3 text-right">Output tokens</th>
                  <th className="py-2 px-3 text-right">$/1M in→out</th>
                  <th className="py-2 pl-3 text-right">Est. cost</th>
                </tr>
              </thead>
              <tbody>
                {data.by_model.map((row) => {
                  const pct = totalReplies > 0 ? Math.round((row.count / totalReplies) * 100) : 0;
                  return (
                    <tr
                      key={`${row.provider}-${row.model}`}
                      className="border-b border-stone-100 last:border-0"
                      data-testid={`model-row-${row.model}`}
                    >
                      <td className="py-3 pr-3">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${providerColor(row.provider)}`}>
                            {row.provider}
                          </span>
                          <span className="font-mono text-stone-700">{row.model}</span>
                          {!row.price_known && (
                            <span className="text-[10px] uppercase text-amber-600" title="No published price for this model — using fallback estimate">
                              est.
                            </span>
                          )}
                        </div>
                        <div className="mt-2 h-1 bg-stone-100 rounded-full overflow-hidden">
                          <div className="h-full bg-[#C9A84C]" style={{ width: `${Math.max(pct, 2)}%` }} />
                        </div>
                        {row.last_used_at && (
                          <p className="text-xs text-stone-400 mt-1">
                            {pct}% of replies · last used {new Date(row.last_used_at).toLocaleString()}
                          </p>
                        )}
                      </td>
                      <td className="py-3 px-3 text-right tabular-nums text-stone-700">{fmtInt(row.count)}</td>
                      <td className="py-3 px-3 text-right tabular-nums text-stone-600">{fmtInt(row.input_tokens)}</td>
                      <td className="py-3 px-3 text-right tabular-nums text-stone-600">{fmtInt(row.output_tokens)}</td>
                      <td className="py-3 px-3 text-right tabular-nums text-stone-500 text-xs">
                        ${row.price_input_per_1m_usd.toFixed(2)} → ${row.price_output_per_1m_usd.toFixed(2)}
                      </td>
                      <td className="py-3 pl-3 text-right tabular-nums font-medium" style={{ color: '#C9A84C' }}>
                        {fmtUsd(row.estimated_cost_usd)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-stone-500">
            No tagged replies yet. Once users chat with Sage, RouteLLM&apos;s model choices — and their spend — will show up here.
          </p>
        )}
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          Last 30 days
        </h3>
        {data.last_30_days?.length ? (
          <ul className="space-y-1 text-sm">
            {data.last_30_days.map((row) => (
              <li key={row.model} className="flex items-center justify-between border-b border-stone-100 py-2 gap-3">
                <span className="font-mono text-stone-700 truncate">{row.model}</span>
                <div className="flex items-center gap-4 shrink-0 text-stone-600 tabular-nums">
                  <span>{fmtInt(row.count)} replies</span>
                  <span className="text-stone-400">{fmtInt(row.input_tokens + row.output_tokens)} tok</span>
                  <span className="font-medium" style={{ color: '#C9A84C' }}>{fmtUsd(row.estimated_cost_usd)}</span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-stone-500">No replies in the last 30 days.</p>
        )}
      </Card>

      <p className="text-xs text-stone-400">{data.note}</p>
    </div>
  );
};

export default ModelsView;
